// ---------------------------------------------------------------------------
// The room, around the round.
//
// Everything that is true for the whole time a player is in a room lives here,
// and nothing that is true for one phase does. A phase screen renders its own
// content and nothing else.
//
// THE CHAT IS THE POINT. It used to be mounted by each screen that wanted one,
// which meant seven copies, three phases with none at all, no chat on a phone
// during play, and two breakpoints that disagreed about when a chat column
// appears. A panel that unmounts is a conversation that stops: the scroll
// position, the draft in the composer and the unread count all went with it,
// every time the game moved. There is exactly one ChatPanel in a room now, it
// is mounted here, and it stays mounted from the join to the last screen.
//
// THE ROOM'S OWN DEADLINE is here for the same reason the chat is. A room that
// is sat in and not used is reclaimed, and both halves of saying so — the header
// button that opens the room-info panel, and the strip that appears for the last
// two minutes — outlive every phase. Putting them on a screen would mean two
// copies that disagree, and a warning that vanishes the moment the game moves.
// See RoomExpiry.tsx for the design and RoomInfo.tsx for the panel, and note the
// one rule there: `extend-room` goes out from a press and from nothing else,
// ever.
//
// ONE BREAKPOINT: 900px, which is `useIsDesktop()` and the width the desktop
// layouts already switch at. Above it the chat is a column beside the screen.
// Below it the same element is a drawer off the right edge, opened from the
// header. It is the same element in both, deliberately — moving the panel
// between two places in the tree at a breakpoint would remount it, which is the
// bug this component exists to remove.
//
// THE Z LADDER, bottom up: the drawer's scrim 20, the drawer 30, DisconnectSheet
// 40, ServerPauseOverlay 50, the leave confirmation 55, toasts 60. The
// confirmation sits above the pause overlay because leaving is allowed during a
// pause, and a dialog a player opened must not end up underneath something that
// arrived afterwards.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ClientGameState, ClientMessage } from '@countdown/shared';
import { AppHeader } from './AppHeader.js';
import { RoomExpiryWarning } from './RoomExpiry.js';
import { RoomInfoButton } from './RoomInfo.js';
import { useRoomExpiry } from './roomExpiryClock.js';
import { ChatPanel } from '../chat/ChatPanel.js';
import { SafetyPanel } from '../../screens/room/lobby/SafetyPanel.js';
import { chatMark, chatPanelShown, unreadChat } from '../chat/chatAccess.js';
import { Button } from '../primitives/Button.js';
import { CloseIcon, Icon, ShieldCheckIcon } from '../primitives/Icon.js';
import { IconButton } from '../primitives/Button.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useKeyboardMode } from '../../hooks/useKeyboardMode.js';
import { swallowNextClick } from '../../lib/swallowNextClick.js';
import { useGameStore } from '../../store/gameStore.js';
import { usePlayerStore } from '../../store/playerStore.js';

export interface RoomShellProps {
  state: ClientGameState;
  send: (message: ClientMessage) => void;
  children: ReactNode;
}

/**
 * A game is running in every phase except the two that bracket it. Leaving from
 * the lobby or the end screen costs nothing and is not worth a dialog; leaving
 * from anywhere else abandons a seat mid-round.
 */
function inProgress(state: ClientGameState): boolean {
  return state.phase !== 'lobby' && state.phase !== 'game-over';
}

export function RoomShell({ state, send, children }: RoomShellProps) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const keyboard = useKeyboardMode();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);

  const you = state.players.find((player) => player.id === state.youId) ?? null;
  const yourTeamId = you?.teamId ?? null;

  // Whether there is a chat in this room at all. `chatMode: 'off'` takes the
  // panel, the drawer, the button and the badge off the screen together — the
  // panel carries nothing but chat (joins and round events go to `state.log`,
  // which nothing draws), so a chat nobody may write in has nothing left to be.
  const chatShown = chatPanelShown(state);

  // Slash reaches the drawer too. The hook takes chat from anywhere, and on a
  // narrow window that would otherwise put the caret in a panel parked off the
  // right edge: the mode would say chat, the screen would stop taking
  // keystrokes, and nothing on screen would have them.
  //
  // Gated on `chatShown` as well, or slash opens a drawer that is not rendered
  // and the screen goes deaf to keystrokes with nothing on it to receive them.
  const chatOpen = chatShown && (drawerOpen || (!isDesktop && keyboard.mode === 'chat'));
  const closeChat = () => {
    setDrawerOpen(false);
    keyboard.releaseChat();
  };

  const chatVisible = chatShown && (isDesktop || chatOpen);

  // Chat being switched off mid-game is the host reaching for a safety control,
  // so it has to land while the panel is open and the caret is in it.
  //
  // Two halves, and they are in two places for a reason. The drawer flag is
  // adjusted DURING the render that notices, the same way `seen` is below and
  // the same way `useHeldTrue` works: setting state from an effect here is a
  // cascading render, and the compiler lint refuses it. Releasing the keyboard
  // is not state, and it has to be an effect because it reaches outside React —
  // the panel releases it for itself, but the shell is what unmounts the panel
  // on this path, so on this path the shell owes the release.
  if (!chatShown && drawerOpen) setDrawerOpen(false);

  useEffect(() => {
    if (!chatShown) keyboard.releaseChat();
  }, [chatShown, keyboard]);

  // **A watermark, not a length, and that is the whole of the fix.**
  //
  // This counted `chat.length - seen` and a deletion used to be a TOMBSTONE, so
  // the array never shrank and the arithmetic held. Deletion is a real removal
  // now, which makes the length non-monotonic: purging six lines drops it below
  // the mark, the difference clamps to zero, and the next six things anybody
  // says light no badge at all — precisely the bug the tombstone was there to
  // prevent, arriving by the other door.
  //
  // `chatMark` is instead the highest `at` this player may read and `unreadChat`
  // counts what is newer than the mark they last saw. Monotonic by construction:
  // removing a line removes a candidate for the maximum and can never raise it,
  // so a purge moves the badge to zero-or-fewer-unread and never past it.
  //
  // Still counted against what this player may actually READ, so a team line
  // nobody outside the team can see does not light a badge for something that
  // will never appear.
  const mark = useMemo(() => chatMark(state, yourTeamId), [state, yourTeamId]);

  // What had been said by the last moment the panel was on screen. Anything
  // arriving after it is what the badge counts.
  //
  // Moved during the render that notices rather than from an effect: an effect
  // would paint one frame of "1 new" over a panel the player is already looking
  // at. It settles in one pass, because the second render finds them equal.
  const [seen, setSeen] = useState(mark);
  if (chatVisible && seen !== mark) setSeen(mark);

  // Unread lives here rather than on a screen, which is the other half of the
  // panel not unmounting: the count used to reset to zero at every phase change
  // because the component holding it was thrown away with the screen.
  const unread = unreadChat(state, yourTeamId, seen);

  // Leaving is an action, not a navigation.
  //
  // Both ways out used to be `navigate('/')` and nothing else, so the seat was
  // never given up: the server kept holding it, and the room went on drawing the
  // player who had gone as waiting. `leave` has been handled server-side the
  // whole time and had no caller anywhere in the client.
  //
  // The token goes with it, unlike a wifi drop: coming back is a new join for a
  // seat this player deliberately gave up. The store is emptied here rather than
  // left for the unmount, so the next room does not open on the last one's
  // roster.
  const leaveRoom = () => {
    send({ type: 'leave' });
    usePlayerStore.getState().clearReconnectToken(state.roomCode);
    useGameStore.getState().clear();
  };

  // The room's own lifetime. Both pieces live here for the same reason the chat
  // does: the deadline outlives every phase, and a warning mounted by a screen
  // would exist twice and would vanish the moment the game moved. It is one
  // line of wiring rather than a hook, because the ONLY thing the client is
  // allowed to do about the deadline is send this from a press. See RoomExpiry.
  const extendRoom = () => send({ type: 'extend-room' });

  // ONE clock for the room's deadline, read here and handed to both pieces. The
  // button and the strip called the hook each, which meant two self-rescheduling
  // chains ticking a second apart for the last ten minutes of every room — one
  // of them driving a component that draws nothing until the last two.
  const expiry = useRoomExpiry(state.roomExpiresAt);

  // Escape closes the drawer. It is also what releases the chat field, and the
  // hook has already handled that by the time this runs.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <AppHeader
        homeGuard={() => {
          // No dialog from the lobby or the end screen, but the seat is still
          // given up on the way out. This is the path most leaves take.
          if (!inProgress(state)) {
            leaveRoom();
            return true;
          }
          setConfirmingLeave(true);
          return false;
        }}
        trailing={
          <>
            <RoomInfoButton
              roomCode={state.roomCode}
              settings={state.settings}
              youAreHost={state.youAreHost}
              expiry={expiry}
              onExtend={extendRoom}
            />
            {/* THE SAFETY PANEL, and why it is here as well as in the lobby.
                `SAFETY_SETTINGS` is the list a host may change while a game is
                RUNNING — chat off, the room locked, permissions withdrawn — and
                the moment any of them is wanted is by definition not the lobby.
                A control that only exists in the lobby is a control that is not
                there when it is needed.

                It is the same component, reading the same `state.settings` and
                sending through the same `send`, so there is no second copy of
                anything to disagree. What there are is two PLACEMENTS: the lobby
                puts its trigger in its own header, among the row of controls a
                host is already looking at, and this one lives in the site header
                for every phase where that row is not on screen. The phase check
                is what keeps it to one at a time — without it the lobby would
                show two shields side by side. */}
            {state.youAreHost && state.phase !== 'lobby' ? (
              <IconButton
                size="sm"
                variant="ghost"
                data-testid="open-safety"
                aria-expanded={safetyOpen}
                label="Room controls"
                title="Chat, the lock and who can do what. Changeable during a game."
                onClick={() => setSafetyOpen(true)}
              >
                <Icon icon={ShieldCheckIcon} size={16} />
              </IconButton>
            ) : null}
            {chatShown ? (
              <ChatButton
                unread={unread}
                open={chatOpen}
                onToggle={() => (chatOpen ? closeChat() : setDrawerOpen(true))}
              />
            ) : null}
          </>
        }
      />

      {/* In flow rather than over the top, so it cannot land on the chat
          composer or behind a phone keyboard, and so the room can see the whole
          screen move when it arrives. */}
      {safetyOpen && state.youAreHost ? (
        <SafetyPanel
          settings={state.settings}
          // The room and the sender are what turn on the Players tab. They are
          // passed HERE and not from the lobby: in the lobby the roster is
          // already on screen with the same powers on every row, and mid-game it
          // is not — which is the whole gap this closes.
          state={state}
          send={send}
          onPatch={(settings) => send({ type: 'update-settings', settings })}
          onClose={() => setSafetyOpen(false)}
        />
      ) : null}

      <RoomExpiryWarning expiry={expiry} onExtend={extendRoom} />

      <div className="flex min-h-0 flex-1">
        {/* The screen. `min-w-0` so a wide board scrolls inside its own pane
            rather than pushing the chat column off the right. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">{children}</div>

        {chatShown && chatOpen && !isDesktop ? (
          <div aria-hidden="true" onClick={closeChat} className="fixed inset-0 z-20 bg-bg/70" />
        ) : null}

        {/* **Gated on `chatShown`, and it was not.** Above 900px this is a
            `static` 340px column with `shrink-0`, so with `chatMode: 'off'` it
            went on holding its width and the room played in a narrowed pane
            beside 340 pixels of nothing. Off means gone, not empty.

            One element, two layouts. Fixed and translated off the right edge on
            a phone, an ordinary column above 900px. A fixed box does not
            contribute to the document's scrollable area, so parking it off
            screen cannot give the page a horizontal scrollbar. */}
        {chatShown ? (
          <div
            id="room-chat"
            inert={!isDesktop && !chatOpen}
            className={`fixed inset-y-0 right-0 z-30 flex w-[min(360px,88vw)] flex-col p-3 transition-transform duration-200 ease-out min-[900px]:static min-[900px]:z-auto min-[900px]:w-[340px] min-[900px]:shrink-0 min-[900px]:translate-x-0 min-[900px]:py-3 min-[900px]:pr-3 min-[900px]:pl-0 ${
              chatOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex shrink-0 justify-end pb-2 min-[900px]:hidden">
              <button
                type="button"
                aria-label="Close chat"
                onClick={closeChat}
                className="grid h-11 w-11 place-items-center rounded-chip border border-line bg-surface text-ink-muted"
              >
                <Icon icon={CloseIcon} size={18} />
              </button>
            </div>
            <ChatPanel state={state} send={send} keyboard={keyboard} className="min-h-0 flex-1" />
          </div>
        ) : null}
      </div>

      {confirmingLeave ? (
        <LeaveConfirm
          onStay={() => setConfirmingLeave(false)}
          onLeave={() => {
            setConfirmingLeave(false);
            leaveRoom();
            navigate('/');
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The phone's way into the chat. Hidden above the breakpoint, where the panel
 * is already on screen and a button to reveal it would say nothing.
 *
 * The badge is a count rather than a dot: "three people said something" and
 * "the room has been talking for a minute" are different reasons to open it.
 * Its width is fixed at two characters and it is capped at 9+, so a busy room
 * cannot widen the button and shuffle the header.
 */
function ChatButton({
  unread,
  open,
  onToggle,
}: {
  unread: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls="room-chat"
      aria-label={unread > 0 ? `Chat, ${unread} unread` : 'Chat'}
      className="relative flex min-h-11 w-[62px] shrink-0 items-center justify-center rounded-[10px] border border-line font-mono text-[11px] font-semibold tracking-[0.12em] text-ink-muted uppercase transition-colors duration-150 hover:border-line-strong hover:text-ink min-[900px]:hidden"
    >
      chat
      {unread > 0 ? (
        <span
          aria-hidden="true"
          className="tabular absolute -top-1.5 -right-1.5 grid h-5 w-6 place-items-center rounded-chip bg-accent font-mono text-[10px] font-bold text-ink-on-fill"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Leaving mid-game.
 *
 * The header's home link is the way out of a room, and on a phone it sits a
 * thumb's width from the board. Abandoning a seat in a live round is not
 * something to do by accident, so it is asked about — once, tersely, and only
 * while a game is actually running.
 */
function LeaveConfirm({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onStay();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onStay]);

  return (
    <div
      data-testid="leave-scrim"
      className="fixed inset-0 z-[55] flex items-center justify-center bg-bg/80 p-6"
      // The darkened area means STAY, which is the safe half of the question and
      // the same thing Escape already meant. It is not a way out of the dialog
      // in the sense the other scrims are — nothing here is dismissed without an
      // answer — it is the cautious answer given by tapping away, which is what
      // somebody who opened this by accident is doing.
      //
      // `swallowNextClick` because the home link that opened this is directly
      // under the scrim: closing on the press unmounts it before the finger
      // comes up, so the click would land on that link and re-ask the question
      // it was just answering.
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        swallowNextClick();
        onStay();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-room-title"
        className="w-full max-w-sm rounded-frame border border-line bg-surface p-6 shadow-lg"
      >
        <h2 id="leave-room-title" className="text-xl font-bold">
          Leave the game?
        </h2>
        <p className="mt-2 text-[15px] text-ink-muted">The round carries on without you.</p>
        <div className="mt-5 flex gap-3">
          {/* Focus lands on staying, not on leaving: the dialog exists because
              the tap that opened it may not have been meant. */}
          <Button autoFocus variant="primary" onClick={onStay} className="flex-1">
            Stay
          </Button>
          <Button variant="danger" onClick={onLeave} className="flex-1">
            Leave
          </Button>
        </div>
      </div>
    </div>
  );
}
